import React, { useEffect, useState } from "react";

interface AttendanceMatrixRowDto {
  userId: number;
  name: string;
  role: string;
  joinTimes: Record<string, string | null>;
}

interface AttendanceMatrixResponse {
  dates: string[];
  rows: AttendanceMatrixRowDto[];
}

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const MentorAttendanceMatrix: React.FC<{ batchId: number }> = ({ batchId }) => {
  const [matrix, setMatrix] = useState<AttendanceMatrixResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/mentor/batch/${batchId}/attendance-matrix`,
      { credentials: "include" }
    )
      .then((res) => res.json())
      .then((data) => {
        setMatrix(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [batchId]);

  if (loading) return <div className="text-gray-600">Loading...</div>;
  if (!matrix || matrix.rows.length === 0)
    return <div className="text-gray-500">No attendance data found.</div>;

  const mentorRows = matrix.rows.filter((row) => row.role === "MENTOR");
  const studentRows = matrix.rows.filter((row) => row.role !== "MENTOR");

  // helper to count days the person has any joinTime
  const getAttendanceCount = (row: AttendanceMatrixRowDto) =>
    matrix.dates.reduce((count, date) => {
      const t = row.joinTimes[date];
      return t && t.trim() !== "" ? count + 1 : count;
    }, 0);

  return (
    <div className="bg-white p-6 rounded-xl shadow">
      <h2 className="text-xl font-bold text-[#009fe3] mb-4">
        Attendance Matrix
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300">
          <thead>
            <tr className="bg-[#f0f8ff] text-[#009fe3]">
              <th className="px-3 py-2 text-left">Name</th>
              {matrix.dates.map((date) => (
                <th key={date} className="px-3 py-2 text-center">
                  {formatDate(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...mentorRows, ...studentRows].map((row) => (
              <tr
                key={row.userId}
                className={
                  row.role === "MENTOR" ? "bg-yellow-100 font-semibold" : ""
                }
              >
                <td className="px-3 py-2">
                  {row.name} {row.role === "MENTOR" && "(Mentor)"}{" "}
                  <span className="ml-1 text-gray-500 font-normal">
                    ({getAttendanceCount(row)})
                  </span>
                </td>
                {matrix.dates.map((date) => (
                  <td key={date} className="px-3 py-2 text-center">
                    {row.joinTimes[date]?.slice(0, 5) || "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MentorAttendanceMatrix;
